import * as React from 'react';
import { usePage } from '@inertiajs/react';
import { assetUrl } from '../../utils/assetUrl';
import { route } from '../../utils/route';
import { trackSignupClicked } from '../../utils/tracking';
import type { BarrioInfo, NeighborhoodDiscount } from '../../types/barrios-landing';

interface BarrioHeroProps {
  barrio: BarrioInfo;
  whatsappUrl: string;
  discount: NeighborhoodDiscount | null;
}

export function BarrioHero({ barrio, whatsappUrl, discount }: BarrioHeroProps) {
  const { cdnUrl } = usePage<{ cdnUrl: string }>().props;
  const heroImg = assetUrl(cdnUrl, '/img/barrios-hero.webp');

  return (
    <section className="bs-hero">
      <style>{`
        .bs-hero {
          position: relative;
          background: url(${heroImg}) center center/cover no-repeat;
          padding: 90px 0 100px;
          min-height: 420px;
          display: flex;
          align-items: center;
        }
        .bs-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.65);
        }
        .bs-hero-inner {
          position: relative;
          z-index: 1;
          max-width: 800px;
          margin: 0 auto;
          padding: 0 24px;
          text-align: center;
        }
        .bs-breadcrumb {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: rgba(255,255,255,0.6);
          margin: 0 0 20px;
        }
        .bs-breadcrumb a {
          color: rgba(255,255,255,0.7);
          text-decoration: none;
        }
        .bs-breadcrumb a:hover {
          color: #fff;
        }
        .bs-hero h1 {
          font-size: 36px;
          font-weight: 800;
          color: #fff;
          margin: 0 0 12px;
          line-height: 1.2;
        }
        .bs-hero .hero-sub {
          font-size: 19px;
          color: rgba(255,255,255,0.85);
          margin: 0 0 10px;
          font-weight: 500;
        }
        .bs-hero .hero-city {
          font-size: 15px;
          color: rgba(255,255,255,0.6);
          margin: 0 0 32px;
        }
        .bs-hero .hero-city i {
          margin-right: 6px;
        }
        .bs-hero-ctas {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }
        .bs-hero-ctas a {
          min-width: 220px;
          justify-content: center;
        }
        .bp-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: #1EAA25;
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          border-radius: 8px;
          text-decoration: none;
          transition: background 0.2s;
        }
        .bp-btn-primary:hover {
          background: #178f1e;
          color: #fff;
          text-decoration: none;
        }
        .bp-btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: transparent;
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          border: 2px solid rgba(255,255,255,0.6);
          border-radius: 8px;
          text-decoration: none;
          transition: all 0.2s;
        }
        .bp-btn-secondary:hover {
          background: rgba(255,255,255,0.1);
          border-color: #fff;
          color: #fff;
          text-decoration: none;
        }
        .bs-hero-discount {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(251, 191, 36, 0.15);
          border: 1px solid rgba(251, 191, 36, 0.4);
          backdrop-filter: blur(8px);
          border-radius: 10px;
          padding: 12px 24px;
          margin: 0 0 24px;
          color: #fde68a;
          font-size: 16px;
          font-weight: 600;
        }
        .bs-hero-discount i {
          font-size: 18px;
          color: #fbbf24;
        }
        .bs-hero-discount strong {
          color: #fff;
          font-weight: 700;
        }
        .bs-hero-discount .discount-code {
          background: rgba(255,255,255,0.15);
          padding: 2px 10px;
          border-radius: 4px;
          font-family: monospace;
          font-size: 15px;
          letter-spacing: 1px;
          color: #fff;
        }
        .bs-hero-discount .discount-apply-link {
          color: #fbbf24;
          text-decoration: none;
          font-size: 14px;
          white-space: nowrap;
        }
        .bs-hero-discount .discount-apply-link:hover {
          color: #fff;
          text-decoration: underline;
        }
        @media (max-width: 640px) {
          .bs-hero {
            padding: 60px 0 70px;
            min-height: auto;
          }
          .bs-hero h1 {
            font-size: 26px;
          }
          .bs-hero .hero-sub {
            font-size: 16px;
          }
          .bs-hero-ctas {
            flex-direction: column;
            align-items: stretch;
          }
          .bp-btn-primary, .bp-btn-secondary {
            justify-content: center;
          }
          .bs-hero-discount {
            font-size: 14px;
            padding: 10px 16px;
          }
        }
      `}</style>
      <div className="bs-hero-inner">
        <nav className="bs-breadcrumb">
          <a href="/">Inicio</a>
          <span>/</span>
          <a href="/barrios-privados">Barrios Privados</a>
          <span>/</span>
          <span style={{ color: 'rgba(255,255,255,0.9)' }}>{barrio.name}</span>
        </nav>
        <h1>Frutas y verduras frescas en {barrio.name}</h1>
        <p className="hero-sub">Recibí productos del Mercado de Abasto directo en tu barrio.</p>
        <p className="hero-city"><i className="fa fa-map-marker-alt" />{barrio.city}</p>
        {discount && (
          <div className="bs-hero-discount">
            <i className="fa fa-tag" />
            <span><strong>{discount.discount}% OFF</strong> con el código <span className="discount-code">{discount.code}</span></span>
            <a href={route('discount.share', { code: discount.code })} className="discount-apply-link">Aplicar descuento →</a>
          </div>
        )}
        <div className="bs-hero-ctas">
          <a href={route('products.index')} className="bp-btn-primary">
            <i className="fa fa-shopping-basket" /> Ver Productos
          </a>
          <a href={route('login')} className="bp-btn-secondary" onClick={() => trackSignupClicked('barrio-show', 'hero')}>
            <i className="fa fa-user-plus" /> Crear una cuenta
          </a>
        </div>
      </div>
    </section>
  );
}
